import { Badge } from '@/global/components/ui/badge'
import { Button } from '@/global/components/ui/button'
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@/global/components/ui/command'
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/global/components/ui/popover'
import { cn } from '@/lib/utils'
import { CheckIcon, ChevronsUpDownIcon, Loader2Icon } from 'lucide-react'
import React, { useContext, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { ShellContext, useEscopoContext } from './context'
import { arr, type FacetaId, LABEL } from './escopo'
import type { Opcao } from './useFacetaOptions'

type Props = {
    id: FacetaId
    multiple?: boolean
    /** lista as opções de todas as empresas do cliente, ignorando o escopo */
    listAll?: boolean
}

/**
 * Parte genérica. Registra a própria multiplicidade e o `listAll` no Root no
 * mount — é esse registro que dá ordem ao rail, diz como zerar a faceta no
 * reset e de onde vêm as opções.
 */
export function Faceta({ id, multiple = false, listAll = false }: Props) {
    const { registrar, ativa, painel } = useEscopoContext()
    const shell = useContext(ShellContext)

    useEffect(
        () => registrar({ id, multiple, listAll }),
        [id, multiple, listAll, registrar],
    )

    const lista = <ListaFaceta id={id} multiple={multiple} />

    if (shell === 'inline') return <FacetaInline id={id}>{lista}</FacetaInline>
    if (!painel || ativa !== id) return null
    return createPortal(lista, painel)
}

/**
 * Lista da faceta. A de funcionário busca e pagina no servidor (#1870), então
 * o `cmdk` não filtra nada nela — filtrar o que já veio esconderia resultado
 * que o servidor mandou. As outras seguem com filtro local.
 */
function ListaFaceta({ id, multiple }: { id: FacetaId; multiple: boolean }) {
    const { valor, setFaceta, dados, busca, setBusca } = useEscopoContext()
    const faceta = dados[id]
    const selecionados = arr(valor[id])
    const vazio = !faceta.carregando && !faceta.opcoes.length
    const isServidor = id === 'funcionarios'

    const [ativos, demitidos] = [
        faceta.opcoes.filter(opcao => opcao.situacao !== 'demitido'),
        faceta.opcoes.filter(opcao => opcao.situacao === 'demitido'),
    ]

    const alternar = (opcaoId: number) => {
        if (!multiple) {
            setFaceta(id, selecionados[0] === opcaoId ? null : opcaoId)
            return
        }
        setFaceta(
            id,
            selecionados.includes(opcaoId)
                ? selecionados.filter(atual => atual !== opcaoId)
                : [...selecionados, opcaoId],
        )
    }

    return (
        <div
            className="flex h-full w-full flex-col"
            onClick={e => e.stopPropagation()}
            onPointerDown={e => e.stopPropagation()}
            onKeyDown={e => {
                if (e.key !== 'Escape') e.stopPropagation()
            }}
        >
            <Command
                shouldFilter={!isServidor}
                className="h-full bg-transparent"
                label={LABEL[id]}
            >
                <CommandInput
                    placeholder={`Buscar em ${LABEL[id].toLowerCase()}…`}
                    className="h-10 text-xs sm:text-sm"
                    {...(isServidor && {
                        value: busca,
                        onValueChange: setBusca,
                    })}
                />
                <CommandList className="min-h-0 max-h-[300px] flex-1 overflow-y-auto bg-transparent p-1">
                    {faceta.carregando && !faceta.opcoes.length && (
                        <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground select-none">
                            <Loader2Icon className="size-4 animate-spin text-primary" />
                            <span>Carregando {LABEL[id].toLowerCase()}…</span>
                        </div>
                    )}
                    {vazio && !faceta.carregando ? (
                        <div className="flex flex-col items-center justify-center gap-1 px-4 py-10 text-center text-sm text-muted-foreground">
                            <p className="font-medium text-foreground/80">
                                {faceta.vazioTexto}
                            </p>
                        </div>
                    ) : (
                        <CommandEmpty className="py-8 text-center text-sm text-muted-foreground">
                            Nenhum resultado encontrado.
                        </CommandEmpty>
                    )}
                    <CommandGroup className="p-0 space-y-0.5">
                        {ativos.map(opcao => (
                            <ItemFaceta
                                key={opcao.id}
                                opcao={opcao}
                                marcada={selecionados.includes(opcao.id)}
                                onSelecionar={() => alternar(opcao.id)}
                            />
                        ))}
                    </CommandGroup>
                    {!!demitidos.length && (
                        <CommandGroup
                            heading="Demitidos"
                            className="p-0 space-y-0.5"
                        >
                            {demitidos.map(opcao => (
                                <ItemFaceta
                                    key={opcao.id}
                                    opcao={opcao}
                                    marcada={selecionados.includes(opcao.id)}
                                    onSelecionar={() => alternar(opcao.id)}
                                />
                            ))}
                        </CommandGroup>
                    )}
                    {faceta.temMais && (
                        <SentinelaProximaPagina
                            onVisivel={faceta.carregarMais}
                            carregando={faceta.carregandoMais}
                        />
                    )}
                </CommandList>
            </Command>
        </div>
    )
}

function ItemFaceta({
    opcao,
    marcada,
    onSelecionar,
}: {
    opcao: Opcao
    marcada: boolean
    onSelecionar: () => void
}) {
    const nivel = opcao.indent ?? 0

    return (
        <CommandItem
            value={`${opcao.descricao ?? ''} ${opcao.label}`}
            onSelect={onSelecionar}
            data-marcada={marcada}
            className={cn(
                'flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm cursor-pointer transition-colors select-none my-0.5',
                'data-[selected=true]:bg-primary/10 data-[selected=true]:text-primary',
                'hover:text-foreground!',
                marcada && 'bg-primary/5 font-medium text-foreground',
            )}
            style={{ paddingLeft: 10 + nivel * 14 }}
        >
            {nivel > 0 && (
                <span
                    aria-hidden
                    className="-ml-1.5 h-3.5 w-0.5 shrink-0 rounded-full bg-border/80"
                />
            )}
            <span
                aria-hidden
                className={cn(
                    'flex size-4 shrink-0 items-center justify-center rounded border transition-all duration-150',
                    marcada
                        ? 'border-primary bg-primary text-primary-foreground shadow-xs'
                        : 'border-input/80 bg-background hover:border-primary/50',
                )}
            >
                {marcada && <CheckIcon className="size-3 stroke-[3]" />}
            </span>
            <span className="truncate flex-1">{opcao.label}</span>
            {opcao.descricao && (
                <span className="shrink-0 font-mono text-[11px] tabular-nums text-muted-foreground/80 bg-muted/60 px-1.5 py-0.5 rounded border border-border/40">
                    {opcao.descricao}
                </span>
            )}
        </CommandItem>
    )
}

/** Pede a próxima página quando entra em tela — rolagem infinita da lista. */
function SentinelaProximaPagina({
    onVisivel,
    carregando,
}: {
    onVisivel?: () => void
    carregando?: boolean
}) {
    const alvo = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const elemento = alvo.current
        if (!elemento || !onVisivel) return

        const observador = new IntersectionObserver(([entrada]) => {
            if (entrada.isIntersecting) onVisivel()
        })

        observador.observe(elemento)
        return () => observador.disconnect()
    }, [onVisivel])

    return (
        <div
            ref={alvo}
            className="flex items-center justify-center py-3 text-xs text-muted-foreground select-none"
        >
            {carregando && (
                <Loader2Icon className="size-4 animate-spin text-primary" />
            )}
        </div>
    )
}

/** No shell inline cada faceta é um botão com popover próprio. */
function FacetaInline({
    id,
    children,
}: {
    id: FacetaId
    children: React.ReactNode
}) {
    const { valor } = useEscopoContext()
    const total = arr(valor[id]).length

    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    className="h-10 min-w-[10rem] justify-between gap-2 font-normal shadow-xs hover:border-primary/40"
                >
                    <span className="truncate">{LABEL[id]}</span>
                    {total > 0 && (
                        <Badge
                            variant="secondary"
                            className="shrink-0 tabular-nums bg-primary/10 text-primary border border-primary/20 font-semibold"
                        >
                            {total}
                        </Badge>
                    )}
                    <ChevronsUpDownIcon className="size-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent
                align="start"
                arrow={false}
                onFocusOutside={e => e.preventDefault()}
                className="w-72 max-w-[calc(100vw-2rem)] overflow-hidden p-0 rounded-xl shadow-xl border-border/80"
            >
                {children}
            </PopoverContent>
        </Popover>
    )
}
